import React, { useMemo } from 'react'
import { Database, Key, Link2, MessageSquare } from 'lucide-react'

interface ViewerAttribute {
  id?: string | number
  name?: string
  type?: string
  isPrimary?: boolean
  isForeign?: boolean
  isRequired?: boolean
  defaultValue?: string
}

interface ViewerEntity {
  id?: string | number
  name?: string
  x?: number
  y?: number
  width?: number
  height?: number
  description?: string
  attributes?: ViewerAttribute[]
  fields?: ViewerAttribute[]
  position?: { x?: number; y?: number }
}

interface ViewerRelationship {
  id?: string | number
  fromEntityId?: string | number
  toEntityId?: string | number
  type?: string
  label?: string
}

interface ViewerForeignKey {
  id?: string | number
  fromEntityId?: string | number
  toEntityId?: string | number
  fromAttributeId?: string | number
  toAttributeId?: string | number
  relationshipType?: string
  label?: string
}

interface ViewerComment {
  id?: string | number
  text?: string
  x?: number
  y?: number
  width?: number
  height?: number
  color?: string
}

interface ViewerZoomPan {
  zoom?: number
  panX?: number
  panY?: number
}

interface DataModelingViewerProps {
  entities?: ViewerEntity[]
  relationships?: ViewerRelationship[]
  foreignKeys?: ViewerForeignKey[]
  comments?: ViewerComment[]
  zoomPan?: ViewerZoomPan | null
  className?: string
}

interface PreparedEntity {
  id: string
  name: string
  x: number
  y: number
  width: number
  height: number
  description?: string
  attributes: ViewerAttribute[]
}

const DEFAULT_ENTITY_WIDTH = 240
const DEFAULT_ENTITY_HEIGHT = 140

const sanitizeNumber = (value: unknown, fallback = 0) => {
  const num = typeof value === 'string' ? Number(value) : value
  return typeof num === 'number' && Number.isFinite(num) ? num : fallback
}

const ensureAttributes = (entity: ViewerEntity): ViewerAttribute[] => {
  if (Array.isArray(entity.attributes)) return entity.attributes
  if (Array.isArray(entity.fields)) return entity.fields
  return []
}

const ensureComments = (comments?: ViewerComment[]) => {
  if (!Array.isArray(comments)) return []
  return comments
}

const DataModelingViewer: React.FC<DataModelingViewerProps> = ({
  entities = [],
  relationships = [],
  foreignKeys = [],
  comments = [],
  zoomPan,
  className = ''
}) => {
  const preparedEntities = useMemo<PreparedEntity[]>(() => {
    return entities.map((entity, index) => {
      const attributes = ensureAttributes(entity)
      const attributeHeight = attributes.length * 28
      const baseHeight = 60 + attributeHeight
      const width = Math.max(DEFAULT_ENTITY_WIDTH, sanitizeNumber(entity.width, DEFAULT_ENTITY_WIDTH))
      const height = Math.max(baseHeight, sanitizeNumber(entity.height, baseHeight))
      const x = sanitizeNumber(entity.x ?? entity.position?.x, 40 + index * 60)
      const y = sanitizeNumber(entity.y ?? entity.position?.y, 40 + index * 40)

      return {
        id: String(entity.id ?? entity.name ?? `entity-${index}`),
        name: entity.name || `Entity ${index + 1}`,
        x,
        y,
        width,
        height,
        description: entity.description,
        attributes
      }
    })
  }, [entities])

  const preparedComments = useMemo(() => {
    return ensureComments(comments).map((comment, index) => ({
      id: String(comment.id ?? index),
      text: comment.text || '',
      x: sanitizeNumber(comment.x, 80 + index * 40),
      y: sanitizeNumber(comment.y, 120 + index * 40),
      width: sanitizeNumber(comment.width, 220),
      height: sanitizeNumber(comment.height, 120)
    }))
  }, [comments])

  const entityMap = useMemo(() => {
    const map = new Map<string, PreparedEntity>()
    preparedEntities.forEach(entity => {
      map.set(entity.id, entity)
    })
    return map
  }, [preparedEntities])

  const relationshipList = useMemo(() => {
    if (!Array.isArray(relationships)) return []
    return relationships.filter(rel => rel && rel.fromEntityId !== undefined && rel.toEntityId !== undefined)
  }, [relationships])

  const foreignKeyList = useMemo(() => {
    if (!Array.isArray(foreignKeys)) return []
    return foreignKeys.filter(fk => fk && fk.fromEntityId !== undefined && fk.toEntityId !== undefined)
  }, [foreignKeys])

  const canvasBounds = useMemo(() => {
    const allX = preparedEntities.map(e => e.x + e.width)
    const allY = preparedEntities.map(e => e.y + e.height)
    const commentX = preparedComments.map(c => c.x + c.width)
    const commentY = preparedComments.map(c => c.y + c.height)
    const maxX = Math.max(600, ...allX, ...commentX)
    const maxY = Math.max(420, ...allY, ...commentY)
    return {
      width: maxX + 120,
      height: maxY + 120
    }
  }, [preparedEntities, preparedComments])

  const zoom = useMemo(() => {
    const parsed = sanitizeNumber(zoomPan?.zoom ?? 1, 1)
    if (!Number.isFinite(parsed) || parsed <= 0) return 1
    return parsed
  }, [zoomPan])

  const panX = useMemo(() => sanitizeNumber(zoomPan?.panX, 0), [zoomPan])
  const panY = useMemo(() => sanitizeNumber(zoomPan?.panY, 0), [zoomPan])

  const containerHeight = useMemo(() => {
    const scaledHeight = canvasBounds.height * Math.min(zoom, 1)
    return Math.min(Math.max(scaledHeight + 160, 480), 1200)
  }, [canvasBounds.height, zoom])

  const renderAttributes = (attributes: ViewerAttribute[]) => {
    if (!attributes.length) {
      return <p className="text-xs text-gray-500">No attributes defined.</p>
    }

    return (
      <ul className="mt-3 space-y-2">
        {attributes.map((attr, index) => (
          <li
            key={String(attr.id ?? index)}
            className="flex items-start justify-between bg-white border border-gray-200 rounded px-3 py-2 text-xs"
          >
            <div className="flex flex-col">
              <span className={`font-mono text-xs text-gray-900 ${attr.isPrimary ? 'font-semibold' : ''}`}>
                {attr.name || 'unnamed_field'}
              </span>
              <span className="text-gray-500">{attr.type || 'type not specified'}</span>
            </div>
            <div className="flex flex-wrap gap-1 justify-end">
              {attr.isPrimary && (
                <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                  <Key className="w-3 h-3" />
                  <span>PK</span>
                </span>
              )}
              {attr.isForeign && (
                <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                  <Link2 className="w-3 h-3" />
                  <span>FK</span>
                </span>
              )}
              {attr.isRequired && (
                <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600">Required</span>
              )}
              {attr.defaultValue && (
                <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Default: {attr.defaultValue}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    )
  }

  const canvasContent = (
    <div
      style={{
        transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
        transformOrigin: '0 0',
        width: canvasBounds.width,
        height: canvasBounds.height,
        position: 'relative'
      }}
    >
      {preparedEntities.length === 0 && preparedComments.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <Database className="w-12 h-12 mx-auto mb-2" />
            <p className="text-sm">No database model saved for this session.</p>
          </div>
        </div>
      )}

      {preparedComments.map(comment => (
        <div
          key={comment.id}
          className="absolute bg-yellow-100 border-2 border-yellow-300 rounded-lg p-3 shadow-lg text-xs text-yellow-900"
          style={{
            left: comment.x,
            top: comment.y,
            width: comment.width,
            minWidth: 160,
            minHeight: 80
          }}
        >
          <div className="flex items-center space-x-2 mb-2 text-yellow-700 font-semibold">
            <MessageSquare className="w-4 h-4" />
            <span>Comment</span>
          </div>
          <p className="whitespace-pre-wrap break-words">{comment.text}</p>
        </div>
      ))}

      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        {relationshipList.map((rel, index) => {
          const fromEntity = entityMap.get(String(rel.fromEntityId))
          const toEntity = entityMap.get(String(rel.toEntityId))
          if (!fromEntity || !toEntity) return null

          const fromX = fromEntity.x + fromEntity.width / 2
          const fromY = fromEntity.y + fromEntity.height / 2
          const toX = toEntity.x + toEntity.width / 2
          const toY = toEntity.y + toEntity.height / 2

          return (
            <g key={String(rel.id ?? index)}>
              <line
                x1={fromX}
                y1={fromY}
                x2={toX}
                y2={toY}
                stroke="#6B7280"
                strokeWidth={2}
              />
            </g>
          )
        })}

        {foreignKeyList.map((fk, index) => {
          const fromEntity = entityMap.get(String(fk.fromEntityId))
          const toEntity = entityMap.get(String(fk.toEntityId))
          if (!fromEntity || !toEntity) return null

          const fromX = fromEntity.x + fromEntity.width
          const fromY = fromEntity.y + fromEntity.height / 2
          const toX = toEntity.x
          const toY = toEntity.y + toEntity.height / 2
          const midX = fromX + (toX - fromX) * 0.65
          const midpointY = fromY + (toY - fromY) / 2

          const fromAttrName = (fromEntity.attributes || []).find(attr => String(attr.id) === String(fk.fromAttributeId))?.name
          const toAttrName = (toEntity.attributes || []).find(attr => String(attr.id) === String(fk.toAttributeId))?.name
          const fkLabel = fk.label || (fromAttrName && toAttrName ? `${fromAttrName} → ${toAttrName}` : 'FK')

          return (
            <g key={String(fk.id ?? index)}>
              <line
                x1={fromX}
                y1={fromY}
                x2={midX}
                y2={fromY}
                stroke="#7C3AED"
                strokeWidth={2}
                strokeDasharray="6,6"
              />
              <line
                x1={midX}
                y1={fromY}
                x2={midX}
                y2={toY}
                stroke="#7C3AED"
                strokeWidth={2}
                strokeDasharray="6,6"
              />
              <line
                x1={midX}
                y1={toY}
                x2={toX}
                y2={toY}
                stroke="#7C3AED"
                strokeWidth={2}
                strokeDasharray="6,6"
                markerEnd="url(#dm-arrowhead)"
              />

              <text
                x={midX + 6}
                y={midpointY - 4}
                fontSize="10"
                textAnchor="start"
                fill="#3B82F6"
                className="font-mono font-semibold"
              >
                {fkLabel}
              </text>
            </g>
          )
        })}
      </svg>

      {preparedEntities.map(entity => (
        <div
          key={entity.id}
          className="absolute bg-white border border-gray-300 rounded-lg shadow-lg"
          style={{
            left: entity.x,
            top: entity.y,
            width: entity.width,
            minWidth: 220
          }}
        >
          <div className="px-4 py-3 bg-gray-800 text-white rounded-t-lg">
            <p className="font-semibold text-sm">{entity.name}</p>
            {entity.description && (
              <p className="text-xs text-gray-200 mt-1 whitespace-pre-wrap break-words">
                {entity.description}
              </p>
            )}
          </div>
          <div className="px-4 py-3 bg-gray-50">
            {renderAttributes(entity.attributes)}
          </div>
        </div>
      ))}

      <defs>
        <marker
          id="dm-arrowhead"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="#7C3AED" />
        </marker>
      </defs>
    </div>
  )

  return (
    <div className={`relative bg-gray-100 border border-gray-200 rounded-xl overflow-hidden ${className}`}>
      <div className="absolute top-3 right-3 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-600 shadow-sm">
        Zoom: {Math.round(zoom * 100)}%
      </div>
      <div
        className="relative overflow-auto"
        style={{
          height: containerHeight
        }}
      >
        {canvasContent}
      </div>
      {(relationshipList.length > 0 || foreignKeyList.length > 0) && (
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur px-3 py-2 rounded-lg text-[11px] text-gray-600 shadow-sm space-y-1">
          {relationshipList.length > 0 && <div>• {relationshipList.length} logical relationship{relationshipList.length === 1 ? '' : 's'}</div>}
          {foreignKeyList.length > 0 && <div>• {foreignKeyList.length} foreign key{foreignKeyList.length === 1 ? '' : 's'}</div>}
        </div>
      )}
    </div>
  )
}

export default DataModelingViewer
